import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Stack,
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';

const DEFAULT_PARAGRAPHS = [
  'We begin each web application assessment by confirming the authorized scope, understanding the application architecture, and identifying the workflows that matter most from both a business and security perspective. The assessment team reviews the exposed functionality, authentication flows, user roles, and input surfaces so we can build an accurate picture of how the application is intended to behave before testing begins.',
  'Once the application surface is mapped, we enumerate reachable pages, routes, parameters, APIs, and supporting components to understand how data moves through the platform. We inspect session handling, role boundaries, user-controlled input, file handling, business logic paths, and client-server interactions to identify where trust assumptions may break down. This phase also helps us fingerprint the observable technology stack and supporting integrations that influence likely attack paths.',
  'With that context established, we perform targeted validation against the application layer. The assessment team tests for issues such as broken access control, authentication weaknesses, injection opportunities, insecure direct object references, cross-site scripting, server-side request forgery, logic abuse, and other flaws that could affect confidentiality, integrity, or availability. Testing is carried out deliberately so findings reflect validated risk and reproducible impact rather than theoretical concerns alone.',
  'Throughout the engagement, we capture evidence, affected locations, and the technical details needed to reproduce each issue. We then translate those results into a report that explains what was identified, how it was verified, what the likely business impact is, and which remediation steps should be prioritized first. The goal is to give both leadership and technical teams a practical, defensible path from assessment through remediation.',
];

const MethodologySection = ({
  primary = '#0A68FF',
  id = 'methodology',
  title = 'Methodology',
  intro = 'This section summarizes how we normally conduct a web application penetration test from application mapping and workflow analysis through validation and reporting.',
  summary = 'We use a staged methodology so each phase builds on verified observations from the phase before it, allowing the assessment team to move from application understanding into focused validation and defensible reporting.',
  paragraphs = DEFAULT_PARAGRAPHS,
}) => {
  const pDark = darken(primary, 0.35);
  const pMid = darken(primary, 0.15);
  const pLite = lighten(primary, 0.28);
  const ink = '#0f172a';

  return (
    <Box
      id={id}
      sx={{
        maxWidth: '8.5in',
        p: '0.72in 0.82in',
        bgcolor: '#fff',
        color: ink,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      <Stack spacing={1.1} sx={{ mb: 1.4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: alpha(ink, 0.8),
            lineHeight: 1.6,
            maxWidth: '92%',
          }}
        >
          {intro}
        </Typography>
      </Stack>

      <Divider
        sx={{
          mb: 2.1,
          borderColor: alpha(ink, 0.08),
          '&::after': {
            content: '""',
            display: 'block',
            height: 4,
            width: 180,
            mt: 1,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${pDark}, ${pMid} 60%, ${pLite})`,
          },
          '@media print': { '&::after': { background: pMid } },
        }}
      />

      <Box
        sx={{
          mb: 1.8,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: alpha(primary, 0.045),
          border: `1px solid ${alpha(primary, 0.14)}`,
        }}
      >
        <Typography variant="body1" sx={{ color: alpha(ink, 0.86), lineHeight: 1.6 }}>
          {summary}
        </Typography>
      </Box>

      <Stack spacing={1.45}>
        {paragraphs.map((paragraph) => (
          <Typography
            key={paragraph}
            variant="body1"
            sx={{
              color: alpha(ink, 0.96),
              lineHeight: 1.65,
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

export default MethodologySection;
